import { matches, parseFilterString } from './filters';
import type { DemoChange, DemoRow } from './types';

/**
 * A stand-in for Supabase Realtime.
 *
 * Realtime uses a websocket, so `global.fetch` does not cover it — and three
 * of the eight `.channel()` call sites live in `packages/hooks`, which cannot
 * import a web-only guard. Patching the client instance (see realtime-shim)
 * reaches all eight from one place.
 *
 * This *implements* realtime rather than disabling it, which is what lets the
 * demo feel alive: RideConfirmation's "finding you a driver" spinner actually
 * advances when the ride flips to `negotiating`, and the negotiation screen
 * updates when the other side moves.
 */

type Listener = (change: DemoChange) => void;

const listeners = new Set<Listener>();

/** Called by the fetch layer after a write. */
export function publish(changes: DemoChange[]): void {
  if (!changes.length || !listeners.size) return;
  for (const change of changes) {
    for (const listener of listeners) {
      // A microtask plus a small delay: realtime is never synchronous with
      // the write in production, and delivering inside the same tick can
      // re-enter React's render.
      setTimeout(() => listener(change), 60);
    }
  }
}

/** Subscribe outside the channel API — the counterpart bot uses this. */
export function onStoreChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

interface Binding {
  event: string;
  table: string;
  filter?: string;
  callback: (payload: {
    eventType: string;
    new: DemoRow | null;
    old: DemoRow | null;
    table: string;
  }) => void;
}

export interface DemoChannel {
  topic: string;
  on: (type: string, config: Record<string, unknown>, cb: Binding['callback']) => DemoChannel;
  subscribe: (cb?: (status: string) => void) => DemoChannel;
  unsubscribe: () => Promise<'ok'>;
}

export function createDemoChannel(topic: string): DemoChannel {
  const bindings: Binding[] = [];
  let unsubscribe: (() => void) | null = null;

  const channel: DemoChannel = {
    topic,

    on(type, config, callback) {
      if (type === 'postgres_changes') {
        bindings.push({
          event: String(config.event ?? '*'),
          table: String(config.table ?? ''),
          filter: config.filter as string | undefined,
          callback,
        });
      }
      return channel;
    },

    subscribe(cb) {
      unsubscribe = onStoreChange((change) => {
        for (const b of bindings) {
          if (b.table !== change.table) continue;
          if (b.event !== '*' && b.event !== change.eventType) continue;

          if (b.filter) {
            const parsed = parseFilterString(b.filter);
            const row = change.new ?? change.old;
            if (parsed && (!row || !matches(row, parsed))) continue;
          }

          b.callback({
            eventType: change.eventType,
            new: change.new,
            old: change.old,
            table: change.table,
          });
        }
      });
      cb?.('SUBSCRIBED');
      return channel;
    },

    async unsubscribe() {
      unsubscribe?.();
      unsubscribe = null;
      return 'ok';
    },
  };

  return channel;
}
